import { Component, inject, Input, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { GeneralObjectKey } from 'src/modules/sm-base/netgen/GeneralObjectKey';
import { GenericDataType } from 'src/modules/enaio/netgen/GenericDataType';
import { SmTableComponent } from 'src/modules/sm-base/components/sm-table/sm-table.component';
import { TableCellButton } from 'src/modules/sm-base/models/table-cell-button.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from 'src/modules/utils/shared/objects';
import { Strings } from 'src/modules/utils/shared/strings';
import { Utils } from 'src/modules/utils/shared/utils';
import { CalendarOutlineTemplateGuiRow } from '../../models/calendar-outline-template-gui-row.model';
import { CalendarEventStartRelativeTo } from '../../netgen/CalendarEventStartRelativeTo';
import { CalendarEventTemplate } from '../../netgen/CalendarEventTemplate';
import { CalendarEventType } from '../../netgen/CalendarEventType';
import { CalendarOutlineEvent } from '../../netgen/CalendarOutlineEvent';
import { CalendarOutlineEventRole } from '../../netgen/CalendarOutlineEventRole';
import { CalendarOutlineLevel } from '../../netgen/CalendarOutlineLevel';
import { CalendarOutlineTemplate } from '../../netgen/CalendarOutlineTemplate';
import { CalendarRole } from '../../netgen/CalendarRole';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { FrontendFieldError } from 'src/modules/sm-base/netgen/FrontendFieldError';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { ProjectPlanningTools } from '../../models/project-planning-tools.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { CalendarEventReminderType } from '../../netgen/CalendarEventReminderType';

@Component({
    selector: 'app-edit-calendar-outline-template-table',
    templateUrl: './edit-calendar-outline-template-table.component.html',
    styleUrl: './edit-calendar-outline-template-table.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditCalendarOutlineTemplateTableComponent {

    service = inject(ProjectPlanningService);
    app = inject(MainAppService);

    @Input()
    forProject = false;

    item: CalendarOutlineTemplate;
    eventTemplates: CalendarEventTemplate[];
    outlineTemplates: CalendarOutlineTemplate[] = [];
    outlineLevels: CalendarOutlineLevel[];
    eventTypes: CalendarEventType[];
    roles: CalendarRole[];
    checkBoxSelection = false;
    existingStructurePaths = new Set<string>();
    beforeDeletePath: (path: string) => Promise<boolean>;

    structure: CalendarOutlineTemplateGuiRow[] = [];
    table: TableData;

    temporaryIdCounter = -100;

    dropDraggedRow = this._dropDraggedRow.bind(this);

    _dropDraggedRow(source: TableRow, dest: TableRow, checkOnly: boolean): Partial<{ allowDrop: boolean;
        dropText: string;
        styleClasses: string[];
    }> {
        let sourceRow = source.raw as CalendarOutlineTemplateGuiRow;
        let destRow = dest.raw as CalendarOutlineTemplateGuiRow;
        if (sourceRow.isTemplate() && destRow.isRealOutline() && !Arrays.equalsFlat(sourceRow.pathItems, destRow.pathItems)) {
            if (!checkOnly) {
                this.moveEvent(sourceRow, destRow);
            }
            return {
                allowDrop: true,
                dropText: this.app.t("entity.CalendarOutlineTemplate.other.dropEventTemplate", { title: this.outlineLevels.find(ol => ol.id == destRow.getLastPath()).title }),
                styleClasses: ["xbg-green-300"]
            };
        }
        else if (sourceRow.isRealOutline() && destRow.isOutline() && !this.isExistingOutline(sourceRow) && sourceRow.path != destRow.path && !destRow.path.startsWith(sourceRow.path)) {
            if (!checkOnly) {
                this.moveLevel(sourceRow, destRow);
            }
            return {
                allowDrop: true,
                dropText: this.app.t("entity.CalendarOutlineTemplate.other.dropLevel", { title: destRow.isRoot() ? this.app.t("entity.CalendarOutlineTemplate.other.root") : this.outlineLevels.find(ol => ol.id == destRow.getLastPath()).title }),
                styleClasses: ["xbg-green-300"]
            };
        }
        else {
            return {
                allowDrop: false,
                dropText: this.app.t("entity.CalendarOutlineTemplate.other.dropNotAllowed"),
                styleClasses: ["xbg-red-300"]
            };
        }
    }    

    moveEvent(source: CalendarOutlineTemplateGuiRow, dest: CalendarOutlineTemplateGuiRow): void {
        source.event.structurePath = dest.path;
        this.fill();
    }

    moveLevel(source: CalendarOutlineTemplateGuiRow, dest: CalendarOutlineTemplateGuiRow): void {
        let sp = source.path;
        let spd = dest.path + (Utils.isNoe(dest.path) ? "" : "|") + source.getLastPath();
        for (let event of this.item.events) {
            if (event.structurePath == sp) {
                event.structurePath  = spd;
            }
            else if (event.structurePath.startsWith(sp + "|")) {
                event.structurePath = spd + "|" + Strings.removePrefix(event.structurePath, sp + "|");
            }
        }
        this.item.structurePaths = this.item.structurePaths.map(sp2 => sp2 == sp ? spd : sp2.startsWith(sp + "|") ? spd + "|" + Strings.removePrefix(sp2, sp + "|") : sp2);
        this.fill();
    }

    @ViewChild("outlineTable") outlineTable: SmTableComponent;

    init(item: CalendarOutlineTemplate, eventTemplates: CalendarEventTemplate[], outlineLevels: CalendarOutlineLevel[], outlineTemplates: CalendarOutlineTemplate[], eventTypes: CalendarEventType[], roles: CalendarRole[], checkBoxSelection: boolean, existingStructurePaths: string[] = []): void {
        this.item = item;
        this.eventTemplates = eventTemplates;
        this.outlineLevels = outlineLevels;
        this.outlineTemplates = outlineTemplates;
        this.eventTypes = eventTypes;
        this.roles = roles;
        this.checkBoxSelection = true; //checkBoxSelection; Deaktiviert - hat zu viele Implikationen. Wenn es wieder reingenommen wird, muss auch in getSelectedEvents der filter-Aufruf wieder aktiviert werden
        this.existingStructurePaths = new Set(existingStructurePaths);
        this.fill();
    }

    fill(): void {
        let outlineDone = new Set<string>();

        this.structure = [new CalendarOutlineTemplateGuiRow("", null)];
        for (let structurePath of Arrays.sort(this.item.structurePaths)) {
            let parts = Strings.split(structurePath, "|");
            for (let i = 1; i < parts.length; i++) {
                parts[i] = parts[i - 1] + "|" + parts[i];
            }
            for (let path of parts) {
                if (!outlineDone.has(path)) {
                    outlineDone.add(path);
                    this.structure.push(new CalendarOutlineTemplateGuiRow(path, null));            
                }
            }
        }
        for (let event of Arrays.reverse(Arrays.sortBy(this.item.events, e => e.getStartDateComparable(this.item.events) ?? ""))) {
            let parentIndex = this.structure.findIndex(row => row.isOutline() && row.path == event.structurePath);
            if (parentIndex == -1) {
                console.log("");
            }
            else {
                this.structure.splice(parentIndex + 1, 0, new CalendarOutlineTemplateGuiRow(null, event));
            }
        }

        this.table = TableData.update(this.table, [
            this.checkBoxSelection ? new TableColumn("create", this.app.t("entity.CalendarOutlineTemplate.other.create"), TableCellType.bool, 1, {
                editable: true
            }) : null,
            new TableColumn("outline", this.app.t("entity.CalendarOutlineTemplate.other.outline"), TableCellType.int, 40, { 
                editable: true,
                editableCallback: params => {
                    let row = params.row.raw as CalendarOutlineTemplateGuiRow;
                    return !row.isRoot() && !this.isExistingOutline(row);
                },
                editor: params => {
                    if ((params.row.raw as CalendarOutlineTemplateGuiRow).isRealOutline()) {
                        let path = (params.row.raw as CalendarOutlineTemplateGuiRow).path;
                        let prefix = Strings.getSubstringToLast(path, "|", false);
                        let exPaths = this.item.structurePaths.filter(sp => sp != path && prefix == Strings.getSubstringToLast(sp, "|", false)).map(sp => Utils.toNumber(Strings.getSubstringFromLast(sp, "|", true)));
                        return new FrontendField("", "", FrontendFieldType.dropdown, {
                            listItems: FrontendFieldListItem.make(this.outlineLevels.filter(ol => !exPaths.includes(ol.id)), item => [item.id, item.title]),
                            dropdownCanFilter: true
                        });
                    }
                    else {
                        return new FrontendField("", "", FrontendFieldType.dropdown, {
                            listItems: FrontendFieldListItem.make(this.eventTemplates, item => [item.id, item.title]),
                            dropdownCanFilter: true
                        });
                    }
                },
                valueSetter: params => {
                    let row = params.row.raw as CalendarOutlineTemplateGuiRow;
                    if (row.isRealOutline()) {
                        let newPath = Arrays.itemsToString([...row.pathItems.slice(0, -1), params.value], "|");
                        this.item.structurePaths[this.item.structurePaths.indexOf(row.path)] = newPath;
                        for (let event of this.item.events) {
                            event.structurePath = event.structurePath == row.path ? newPath : event.structurePath.startsWith(row.path) ? newPath + Strings.removePrefix(event.structurePath, row.path) : event.structurePath;
                        }
                        this.item.structurePaths = this.item.structurePaths.map(sp => sp.startsWith(row.path) ? newPath + Strings.removePrefix(sp, row.path) : sp);
                        row.setPath(newPath);
                        (params.row.values.outline as TableCell).value = params.value;
                    }
                    else if (row.isTemplate() && row.event.templateId != params.value) {
                        row.event.setTemplate(this.eventTemplates.find(item => item.id == params.value));   
                        this.makeRow(row, params.row);
                        this.outlineTable.refresh();
                    }
                },
                cellRenderer: params => {
                    let row = params.data.raw as CalendarOutlineTemplateGuiRow;
                    let isRoot = row.isRoot();
                    let isOutline = row.isOutline();
                    return "<i class=\"node-level-" + row.getLevel() + " mr-2 " + (isOutline ? isRoot ? "fas fa-asterisk text-green-500" : "fas fa-folder text-yellow-500" : "fas fa-calendar") + "\"></i>" +
                        (isRoot ? this.app.t("entity.CalendarOutlineTemplate.other.root") : !Utils.isNoe(params.value) ? isOutline ? this.outlineLevels.find(l => l.id == params.value)?.title ?? ""
                                          : this.eventTemplates.find(t => t.id == params.value)?.title ?? ""
                                      : isOutline ? this.app.t("entity.CalendarOutlineTemplate.other.outlineLevelPlaceholder") : this.app.t("entity.CalendarOutlineTemplate.other.taskPlaceholder"));
                }
            }),
            ...this.makeAttribColumns(new TableColumn("title", this.app.t("entity.CalendarOutlineEvent.fields.title"), TableCellType.string, 30)),
            new TableColumn("numTasks", this.app.t("entity.CalendarEventTemplate.other.numTasks"), TableCellType.int, 2, {
                align: "center"
            }),          
            ...this.makeAttribColumns(new TableColumn("typeId", this.app.t("entity.CalendarOutlineEvent.fields.typeId"), TableCellType.int, 15, {
                editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                    listItems: FrontendFieldListItem.make(this.eventTypes, item => [item.id, item.title]),
                    dropdownCanFilter: true
                }),
                valueFormatter: e => this.eventTypes.find(item => item.id == e.value)?.title ?? ""
            })),          
            ...this.makeAttribColumns(new TableColumn("isMilestone", this.app.t("entity.CalendarOutlineTemplate.other.isMilestoneShort"), TableCellType.bool, 1, {
                toolTip: this.app.t("entity.CalendarOutlineEvent.fields.isMilestone")
            })),     
            ...this.makeAttribColumns(new TableColumn("durationDays", this.app.t("entity.CalendarOutlineTemplate.other.durationShort"), TableCellType.int, 3, {
                toolTip: this.app.t("entity.CalendarOutlineTemplate.other.durationTooltip")
            })),
            ...this.makeAttribColumns(new TableColumn("onlyWeekdays", this.app.t("entity.CalendarOutlineTemplate.other.onlyWeekdaysShort"), TableCellType.bool, 1, {
                toolTip: this.app.t("entity.CalendarOutlineEvent.fields.onlyWeekdays")
            })),
            ...this.makeAttribColumns(new TableColumn("workingTime", this.app.t("entity.CalendarOutlineTemplate.other.workingTimeShort"), TableCellType.decimal, 5, {
                toolTip: this.app.t("entity.CalendarOutlineTemplate.other.workingTimeTooltip"),
                numDecimalDigits: 2,
                valueFormatter: e => (e.row.raw as CalendarOutlineTemplateGuiRow).isOutline() ? "" : Utils.formatNumber(Utils.toNumber(e.value), 2, true, false, false),
                valueSetter: e => Utils.roundToDecimal(e.value as number, 0.25)
            })),
            ...this.makeAttribColumns(new TableColumn("startRelativeDays", this.app.t("entity.CalendarOutlineTemplate.other.startRelativeDaysShort"), TableCellType.int, 3)),
            ...this.makeAttribColumns(new TableColumn("startRelativeTo", this.app.t("entity.CalendarOutlineEvent.fields.startRelativeTo"), TableCellType.object, 15, {
                editor: (e: TableCellEvent) => new FrontendField("", "", FrontendFieldType.dropdown, {
                    dropdownCanFilter: true,
                    listItems: [
                        ...FrontendFieldListItem.make(Utils.getEnumValues(CalendarEventStartRelativeTo).filter(v => (v as CalendarEventStartRelativeTo) != CalendarEventStartRelativeTo.task), item => [item, CalendarEventStartRelativeTo.getDisplayName(item, this.app)], false),
                        ...FrontendFieldListItem.make(this.item.events.filter(evt => (e.row.raw as CalendarOutlineTemplateGuiRow).event != null && evt.id != (e.row.raw as CalendarOutlineTemplateGuiRow).event.id), 
                            item => [item, item.getTitle() + " (" + this.service.formatStructurePathString(item.structurePath) + ")", { comparable: item.getStartDateComparable(this.item.events) }], true)
                    ]
                }),
                valueGetter: e => {
                    let row = e.row.raw as CalendarOutlineTemplateGuiRow;
                    if (row.event == null) {
                        return 0;
                    }
                    if (row.event.getStartRelativeTo() == CalendarEventStartRelativeTo.task) {
                        return this.item.events.find(evt => evt.id == row.event.startRelativeToEventId);
                    }          
                    else {
                        return row.event.getStartRelativeTo();
                    }
                },
                valueSetter: e => {
                    let row = e.row.raw as CalendarOutlineTemplateGuiRow;
                    let oldRel = row.event.getRelativeStartDate(this.item.events);
                    let oldStartRelativeTo = row.event.startRelativeTo;
                    if (e.value instanceof CalendarOutlineEvent) {
                        row.event.startRelativeToEventId = e.value.id;
                        row.event.startRelativeTo = CalendarEventStartRelativeTo.task;
                        if (oldStartRelativeTo != CalendarEventStartRelativeTo.task) {
                            row.event.keepStartRelativeTo = true;
                            e.row.values.keepStartRelativeTo = true;
                        }
                    }
                    else {
                        row.event.startRelativeToEventId = null;
                        row.event.startRelativeTo = e.value as CalendarEventStartRelativeTo;
                        if (oldStartRelativeTo == CalendarEventStartRelativeTo.task) {
                            row.event.keepStartRelativeTo = false;
                            e.row.values.keepStartRelativeTo = false;
                        }
                    }
                    let newRel = row.event.getRelativeStartDate(this.item.events);
                    row.event.startRelativeDays = row.event.getStartRelativeDays() - (newRel.days - oldRel.days);
                    console.log(Objects.toJson(oldRel) + " " + Objects.toJson(newRel));
                },
                valueFormatter:  e => {
                    let row = e.row.raw as CalendarOutlineTemplateGuiRow;
                    if (row.event == null) {
                        return "";
                    }
                    if (row.event.getStartRelativeTo() == CalendarEventStartRelativeTo.task) {
                        return this.item.events.find(evt => evt.id == row.event.startRelativeToEventId)?.getTitle();
                    }
                    return CalendarEventStartRelativeTo.getDisplayName(e.value as CalendarEventStartRelativeTo, this.app) ?? "";
                }
            })),
            new TableColumn("keepStartRelativeTo", this.app.t("entity.CalendarOutlineTemplate.other.keepStartRelativeToShort"), TableCellType.bool, 1, {
                toolTip: this.app.t("entity.CalendarOutlineTemplate.other.keepStartRelativeToTooltip"),
                editable: true,
                valueSetter: e => {
                    let row = e.row.raw as CalendarOutlineTemplateGuiRow;
                    row.event.keepStartRelativeTo = e.value;
                    e.row.values.keepStartRelativeTo = e.value; 
                }          
            }),
            ...this.makeAttribColumns(new TableColumn("roles", this.app.t("entity.CalendarOutlineEvent.fields.roles"), TableCellType.object, 50, {
                valueFormatter: e => Arrays.is(e.value) ? Arrays.itemsToString(Arrays.sort((e.value as CalendarOutlineEventRole[]).map(item => (this.roles.find(r => r.id == item.roleId)?.title ?? "???") + ": " + item.count))) : "",
                editorPopup: true,
                editor: new FrontendField("", "", FrontendFieldType.table, {
                    tableColumns: [
                        new TableColumn("roleId", this.app.t("entity.CalendarOutlineEventRole.fields.roleId"), TableCellType.int, 20, {
                            editable: true,
                            editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                                listItems: FrontendFieldListItem.make(this.roles, item => [item.id, item.title]),
                                dropdownCanFilter: true
                            }),
                            valueFormatter: e => this.roles.find(item => item.id == e.value)?.title ?? ""
                        }),
                        new TableColumn("count", this.app.t("entity.CalendarOutlineEventRole.fields.count"), TableCellType.int, 2, {
                            editable: true
                        })    
                    ],
                    style: { "min-width": "300px" },
                    userDefinedData: { cloneValueForTableCellEditor: true }
                })
            })),
            ...this.makeAttribColumns(new TableColumn("reminderType", this.app.t("entity.CalendarOutlineTemplate.other.reminderTypeShort"), TableCellType.int, 10, {
                editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                    listItems: FrontendFieldListItem.make(Utils.getEnumValues(CalendarEventReminderType), item => [item, CalendarEventReminderType.getDisplayName(item, this.app)], false)
                }),
                valueFormatter: e => {
                    let row = e.row.raw as CalendarOutlineTemplateGuiRow;
                    if (row.event == null) {
                        return "";
                    }
                    return CalendarEventReminderType.getDisplayName(e.value as CalendarEventReminderType, this.app) ?? "";
                }
            })),
            ...this.makeAttribColumns(new TableColumn("reminderDeltaMinutes", this.app.t("entity.CalendarOutlineTemplate.other.reminderDeltaMinutesShort"), TableCellType.int, 10, {
                toolTip: this.app.t("entity.CalendarOutlineTemplate.other.reminderDeltaMinutesTooltip")
            }))    
        ], this.structure.map(row => this.makeRow(row, null)));
        this.table.useRowIds = true;
    }

    async addTemplate(parent: CalendarOutlineTemplateGuiRow): Promise<void> {
        this.outlineTemplates = await this.service.getOutlineTemplates(true);
        let f = new FrontendField("", this.app.t("entity.CalendarOutlineTemplate.other.insertOutlineTemplatesField"), FrontendFieldType.listBox, {
            listBoxCheckBoxes: true,
            dropdownCanFilter: true,            
            listItems: FrontendFieldListItem.make(this.outlineTemplates, ot => [ot.id, ot.title], true)
        });

        let outlineTemplateIds = await this.app.dialog.input<number[]>(f, this.app.t("entity.CalendarOutlineTemplate.other.insertOutlineTemplatesDialog"));
        if (Utils.isNoe(outlineTemplateIds)) {
            return;
        }

        let outlineTemplates: CalendarOutlineTemplate[] = [];
        for (let id of outlineTemplateIds) {
            let full = await ProjectPlanningTools.definitionCalendarOutlineTemplate.restGet(id);
            if (full == null) {
                this.outlineTemplates = await this.service.getOutlineTemplates(true);
                this.app.showErrorToast(this.app.t("entity.CalendarOutlineTemplate.other.selectedTemplateNoLongerAvailable"));
                return;
            }
            full.initForAddProject(this.eventTemplates);
            outlineTemplates.push(full);
        }

        for (let full of outlineTemplates) {
            for (let event of full.events) {
                event.structurePath = Strings.combine(parent.path, event.structurePath, "|");
                this.item.events.push(event);    
                this.item.structurePaths.push(event.structurePath);
            }
            this.item.structurePaths = Arrays.getUnique(this.item.structurePaths);
        }
        this.fill();
    }

    addPath(parent: CalendarOutlineTemplateGuiRow): void {
        let prefix = !Utils.isNoe(parent.path) ? parent.path + "|" : "";
        let firstFree = this.outlineLevels.find(ol => !this.item.structurePaths.includes(prefix + Objects.toString(ol.id)));
        if (firstFree == null) {
            this.app.showErrorToast(this.app.t("entity.CalendarOutlineTemplate.other.noMoreOutlineLevels"));
            return;
        }
        this.item.structurePaths.push(prefix + 0);
        let oldRowIds = this.table.rows.map(r => r.id);
        this.fill();
        let added = this.table.rows.map(r => r.id).filter(id => !id.includes("^") && !oldRowIds.includes(id));
        if (added.length > 0) {
            GuiUtils.angularTimer(() => this.outlineTable.makeRowVisible(added[0]));
        }
    }

    async deletePath(row: CalendarOutlineTemplateGuiRow): Promise<void> {
        if (this.beforeDeletePath != null && !await this.beforeDeletePath(row.path)) {
            return;
        }

        this.item.structurePaths = this.item.structurePaths.filter(sp => row.path != "" && sp != row.path && !sp.startsWith(row.path + "|"));
        this.item.events = this.item.events.filter(e => row.path != "" && e.structurePath != row.path && !e.structurePath.startsWith(row.path + "|"));
        this.fill();
    }

    deleteEvent(row: CalendarOutlineTemplateGuiRow): void {
        this.item.events = Arrays.without(this.item.events, row.event);

        for (let event of this.item.events) {
            if (event.getStartRelativeTo() == CalendarEventStartRelativeTo.task && event.startRelativeToEventId == row.event.id) {
                if (row.event.getStartRelativeTo() == CalendarEventStartRelativeTo.task) {
                    let linkedEvent = this.item.events.find(e => e.id == row.event.startRelativeToEventId);
                    if (linkedEvent != null) {
                        event.startRelativeToEventId = linkedEvent.id;
                        event.startRelativeDays = event.getStartRelativeDays() + row.event.getStartRelativeDays();
                    }
                }
                else {
                    event.startRelativeTo = row.event.getStartRelativeTo();
                    event.startRelativeToEventId = null;
                    event.startRelativeDays = event.getStartRelativeDays() + row.event.getStartRelativeDays();
                }
            }
        }

        this.fill();
    }

    addEvent(parent: CalendarOutlineTemplateGuiRow): void {
        this.item.events.push(Objects.fromPlain(CalendarOutlineEvent, {
            id: this.temporaryIdCounter--,
            structurePath: parent.path,
            sortIndex: 0,
            templateId: 0,
            template: null
        }));

        let oldRowIds = this.table.rows.map(r => r.id);
        this.fill();
        let added = this.table.rows.map(r => r.id).filter(id => !oldRowIds.includes(id));
        if (added.length > 0) {
            GuiUtils.angularTimer(() => this.outlineTable.makeRowVisible(added[0]));
        }
    }

    async addEvents(parent: CalendarOutlineTemplateGuiRow): Promise<void> {
        let f = new FrontendField("", this.app.t("entity.CalendarOutlineTemplate.other.insertTasksField"), FrontendFieldType.listBox, {
            listBoxCheckBoxes: true,
            dropdownCanFilter: true,
            listItems: FrontendFieldListItem.make(this.eventTemplates, et => [et.id, et.title], true)
        });

        let templateIds = await this.app.dialog.input<number[]>(f, this.app.t("entity.CalendarOutlineTemplate.other.insertTasksDialog"));
        if (Utils.isNoe(templateIds)) {
            return;
        }

        for (let templateId of templateIds) {
            let evt = Objects.fromPlain(CalendarOutlineEvent, {                
                id: this.temporaryIdCounter--,
                structurePath: parent.path,
                sortIndex: 0,
                templateId,
                template: null
            });
            evt.setTemplate(this.eventTemplates.find(item => item.id == templateId));   
            this.item.events.push(evt);

        }
        this.fill();
    }

    makeAttribColumns(col: TableColumn, editable = true): TableColumn[] {
        col.editable = editable;
        col.sortable = false;
        col.editableCallback = this.isOutlineCellEditable.bind(this);
        col.styleClass = "no-right-border";
        let oldSetter = col.valueSetter;
        col.valueSetter = e => {
            if (oldSetter != null) {
                oldSetter(e);
            }
            this.cellChanged(e);
        };
        return [
            col,
            new TableColumn("override" + Strings.upperFirst(col.id), "", TableCellType.button, 0.5, {
                maxWidth: 15,
                editable: false
            })
        ];
    }

    makeRow(row: CalendarOutlineTemplateGuiRow, updateRow: TableRow): TableRow {
        updateRow ??= new TableRow(row.getRowId(), row);
        updateRow.canDrag = !row.isRoot();

        updateRow.values.outline = new TableCell(row.getOutlineElement(), { 
            internal: [new GeneralObjectKey("outline", row)], 
            actionsAsContextMenu: true,
            actionProviders: [
                {
                    getActions: value => {
                        let guiRow = value.key as CalendarOutlineTemplateGuiRow;
                        return Arrays.withoutNull([
                            this.forProject && guiRow.isOutline() ? {
                                getCaption: _ => this.app.t("entity.CalendarOutlineTemplate.other.insertOutlineTemplateHere"),
                                getIcon: _ => "fas fa-plus",
                                isUsable: _ => guiRow.isOutline(),
                                perform: async (_) => {
                                    await this.addTemplate(guiRow);
                                    return "";
                                }
                            } : null,
                            guiRow.isOutline() ? {
                                getCaption: _ => this.app.t("entity.CalendarOutlineTemplate.other.insertSubLevel"),
                                getIcon: _ => "fas fa-plus",
                                isUsable: _ => guiRow.isOutline(),
                                perform: _ => {
                                    this.addPath(guiRow);
                                    return "";
                                }
                            } : null,
                            guiRow.isOutline() ? {
                                getCaption: _ => this.app.t("entity.CalendarOutlineTemplate.other.deleteLevel"),
                                getIcon: _ => "fas fa-trash",
                                isUsable: _ => guiRow.isOutline(),
                                perform: async _ => {
                                    await this.deletePath(guiRow);
                                    return "";
                                }
                            } : null,
                            guiRow.isRealOutline() ? {
                                getCaption: _ => this.app.t("entity.CalendarOutlineTemplate.other.insertTask"),
                                getIcon: _ => "fas fa-calendar",
                                isUsable: _ => guiRow.isOutline(),
                                perform: _ => {
                                    this.addEvent(guiRow);
                                    return "";
                                }
                            } : null,
                            guiRow.isRealOutline() ? {
                                getCaption: _ => this.app.t("entity.CalendarOutlineTemplate.other.insertMultipleTasks"),
                                getIcon: _ => "fas fa-calendar-check",
                                isUsable: _ => guiRow.isOutline(),
                                perform: async (_) => {
                                    await this.addEvents(guiRow);
                                    return "";
                                }
                            } : null,
                            !guiRow.isOutline() ? {
                                getCaption: _ => this.app.t("entity.CalendarOutlineTemplate.other.deleteTask"),
                                getIcon: _ => "fas fa-trash",
                                isUsable: _ => !guiRow.isOutline(),
                                perform: _ => {
                                    this.deleteEvent(guiRow);
                                    return "";
                                }
                            } : null
                        ]);
                    }
                }
            ]
        });
        for (let attrib of CalendarOutlineEvent.attribs) {
            let override = row.event?.isOverridden(attrib.name) ?? false;
            if (row.isOutline()) {
                updateRow.values[attrib.name] = attrib.type == GenericDataType.bool ? null : "";
            }
            else {
                let cell = new TableCell((row.event != null ? row.event.getAttrib(attrib.name) : null) ?? GenericDataType.getDefaultValue(attrib.type), { styleClass: override ? "no-right-border xbg-yellow-100" : null });            
                if (Arrays.is(cell.value)) {
                    cell.value = Objects.cloneDeep(cell.value);
                }
                updateRow.values[attrib.name] = cell;
            }
            updateRow.values["override" + Strings.upperFirst(attrib.name)] = override ? new TableCell([new TableCellButton("setToDefault", "fas fa-times")], { styleClass: "xbg-yellow-100"}) : [];
        }
        updateRow.values.create = row.isOutline() || row.isRoot() ? null : true;
        updateRow.values.numTasks = row.isTemplate() ? row.event.template?.subTasks?.length ?? 0
            : "";
        updateRow.values.keepStartRelativeTo = row.event?.keepStartRelativeTo;

        return updateRow;
    }

    isOutlineCellEditable(event: TableCellEvent): boolean {
        return !(event.row.raw as CalendarOutlineTemplateGuiRow).isOutline();
    }

    isExistingOutline(row: CalendarOutlineTemplateGuiRow): boolean {
        return row.isRealOutline() && this.existingStructurePaths.has(row.path);
    }

    cellClicked(event: TableCellEvent): void {
        let row = event.row.raw as CalendarOutlineTemplateGuiRow;
        if (event.isButton("setToDefault", null)) {
            let attribName = Strings.lowerFirst(Strings.removePrefix(event.column.colId, "override"));
            let attrib = CalendarOutlineEvent.attribs.find(item => item.name == attribName);
            if (attrib != null) {
                row.event[attrib.name] = null;
                if (attrib.name == "roles") {
                    row.event.inheritRoles = true;
                }
                else if (attrib.name == "startRelativeTo" || attrib.name == "startRelativeDays") {
                    if (attrib.name == "startRelativeTo") {
                        row.event.startRelativeDays = null;
                        event.row.values.overrideStartRelativeDays = false;

                    }
                    else if (attrib.name == "startRelativeDays") {
                        row.event.startRelativeTo = null;
                        event.row.values.overrideStartRelativeTo = false;
                    }
                    row.event.keepStartRelativeTo = row.event.getStartRelativeTo() == CalendarEventStartRelativeTo.task;
                    event.row.values.keepStartRelativeTo = row.event.keepStartRelativeTo;
                }
                event.row.values["override" + Strings.upperFirst(attrib.name)] = false;
                this.makeRow(row, event.row);
                this.outlineTable.refresh();
            }
        }
    }


    cellChanged(event: TableCellEvent): void {
        let row = event.row.raw as CalendarOutlineTemplateGuiRow;
        let attrib = CalendarOutlineEvent.attribs.find(item => item.name == event.column.colId);
        if (attrib != null) {
            let value = TableCell.getValue(event.value);
            let changed = Arrays.is(value) ? !Objects.equalsDeep(value as any[], row.event.getAttrib(attrib.name) as any[]) : value !== row.event.getAttrib(attrib.name);
            if (changed) {
                if (attrib.name != "startRelativeTo") {
                    row.event[attrib.name] = value;
                }
                if (attrib.name == "roles") {
                    row.event.inheritRoles = false;
                }
                event.row.values["override" + Strings.upperFirst(attrib.name)] = true;
                this.makeRow(row, event.row);
                this.outlineTable.refresh();
            }  
        }
    }

    validate(): FrontendFieldError[] {
        let result: FrontendFieldError[] = [];

        if (Utils.isNoe(this.getStructurePaths())) {
            result.push(new FrontendFieldError("templateId", this.app.t("entity.CalendarOutlineTemplate.other.validationNoOutlineLevel")));    
        }

        for (let sp of this.item?.structurePaths ?? []) {
            let parts = Strings.split(sp, "|");
            if (parts.includes("0")) {
                result.push(new FrontendFieldError("templateId", this.app.t("entity.CalendarOutlineTemplate.other.validationOutlineLevelUnassigned")));               
            }
        }
        for (let event of this.item?.events ?? []) {
            if (event.templateId == null || event.templateId == 0) {
                result.push(new FrontendFieldError("templateId", this.app.t("entity.CalendarOutlineTemplate.other.validationTaskTemplateMissing")));   
            }
        }

        return result;
    }

    getStructurePaths(): string[] {
        return this.structure.filter(row => row.isRealOutline()).map(row => row.path);
    }

    getSelectedEvents(): CalendarOutlineEvent[] {
        return this.table.rows.filter(row => (row.raw as CalendarOutlineTemplateGuiRow).isTemplate())/*.filter(row => row.values.create)*/.map(row => (row.raw as CalendarOutlineTemplateGuiRow).event);
    }
}
